import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { supabase, getUserProfile } from '../lib/supabase';
import { dateKey } from '../lib/availability';
import { Save } from 'lucide-react';
import {
  WEEKDAY_SHORT, MONTH_SHORT,
  fmtFullDate, todayMidnight, isPastDay, isBeyondHorizon,
  startOfWeek, weekDays, monthGridDays,
  timeToMin, minToTime,
} from '../lib/barberSchedule/helpers';
import { PolicyPanel } from '../components/barberSchedule/PolicyPanel';
import { DirtyBar } from '../components/barberSchedule/DirtyBar';
import { ForceOpenModal } from '../components/barberSchedule/ForceOpenModal';
import { ViewNav } from '../components/barberSchedule/ViewNav';
import { AvailabilityGrid, AvailabilityActions } from '../components/barberSchedule/AvailabilityGrid';
import './BarberSchedule.css';

/**
 * BarberSchedule — self-serve page for a barber to control:
 *   1. Minimum notice they need before a booking (dropdown preset)
 *   2. How far ahead customers can book them (dropdown preset)
 *   3. Date exceptions — block a day (holiday) OR force-open a
 *      normally-off day (with custom start/end times)
 *   4. Day-view: paint breaks by tapping slots on the availability grid.
 *
 * Route: /settings/schedule
 * Access: any authenticated barber sees their own row. shop_admin and
 * master_admin can also land here (defaults to their own barber row
 * if they have one; otherwise a friendly empty state).
 */
const BarberSchedule = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetBarberId = searchParams.get('barber');
  const [barber, setBarber] = useState(null);
  const [callerRole, setCallerRole] = useState(null);
  const [weeklyHours, setWeeklyHours] = useState([]);
  const [exceptions, setExceptions] = useState({}); // date -> row
  const [minNotice, setMinNotice] = useState(24);
  const [bookingWindow, setBookingWindow] = useState(14);
  const [slotInterval, setSlotInterval] = useState(0);
  const [bookingBuffer, setBookingBuffer] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');

  // Snapshot of last-saved values so we can compute a `dirty` flag and
  // render the sticky "unsaved changes" bar (fix 3.6). Snapshot is
  // reset after every load() and save() so the bar only appears when
  // the barber genuinely has pending edits.
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const currentSnapshot = useMemo(() => JSON.stringify({
    minNotice, bookingWindow, slotInterval, bookingBuffer,
    weeklyHours,
    // Only include exceptions the user actually touched — untouched
    // date-exceptions carry no `_dirty` flag and shouldn't dirty the bar.
    exceptions: Object.values(exceptions || {}).filter((e) => e && e._dirty),
  }), [minNotice, bookingWindow, slotInterval, bookingBuffer, weeklyHours, exceptions]);
  const dirty = !!savedSnapshot && savedSnapshot !== currentSnapshot;

  const [openEditor, setOpenEditor] = useState(null);
  const [dayView, setDayView] = useState(null);

  // View switcher (Feb 2026): "day" | "week" | "month". Session-only.
  const [viewMode, setViewMode] = useState('week');
  const [viewAnchor, setViewAnchor] = useState(() => todayMidnight());

  useEffect(() => { load(); }, [targetBarberId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate('/signin'); return; }

      const { data: profile } = await getUserProfile(user.id);
      setCallerRole(profile?.role || null);

      let brow = null;
      if (targetBarberId && (profile?.role === 'master_admin' || profile?.role === 'shop_admin')) {
        const { data, error: bErr } = await supabase
          .from('barbers').select('*').eq('id', targetBarberId).maybeSingle();
        if (bErr) throw bErr;
        if (profile.role === 'shop_admin' && data && data.shop_id !== profile.shop_id) {
          setError("You don't have permission to edit this barber's schedule.");
          setLoading(false);
          return;
        }
        brow = data;
      } else {
        const { data, error: bErr } = await supabase
          .from('barbers').select('*').eq('user_id', user.id).eq('active', true).maybeSingle();
        if (bErr) throw bErr;
        brow = data;
      }

      if (!brow) { setBarber(null); setLoading(false); return; }

      setBarber(brow);
      setMinNotice(brow.min_notice_hours || 24);
      setBookingWindow(brow.booking_window_days || 14);
      setSlotInterval(brow.slot_interval_minutes || 0);
      setBookingBuffer(brow.booking_buffer_minutes || 0);

      const [hoursRes, excRes] = await Promise.all([
        supabase.from('barber_hours')
          .select('weekday,start_time,end_time,is_open')
          .eq('barber_id', brow.id),
        supabase.from('barber_date_exceptions')
          .select('id,date,is_closed,start_time,end_time')
          .eq('barber_id', brow.id),
      ]);

      setWeeklyHours(hoursRes.data || []);
      const map = {};
      (excRes.data || []).forEach((e) => { map[e.date] = e; });
      setExceptions(map);
      setTimeout(() => setSavedSnapshot(JSON.stringify({
        minNotice: brow.min_notice_hours || 24,
        bookingWindow: brow.booking_window_days || 14,
        slotInterval: brow.slot_interval_minutes || 0,
        bookingBuffer: brow.booking_buffer_minutes || 0,
        weeklyHours: hoursRes.data || [],
        exceptions: [],
      })), 0);
    } catch (e) {
      setError(e.message || 'Failed to load schedule.');
    } finally {
      setLoading(false);
    }
  };

  const weeklyOpenOn = (weekday) => {
    const row = weeklyHours.find((r) => r.weekday === weekday);
    return !!(row && row.is_open !== false && row.start_time && row.end_time);
  };

  const usualHours = useMemo(() => {
    const open = weeklyHours.find((r) => r.is_open !== false && r.start_time && r.end_time);
    return open ? { start: open.start_time.slice(0, 5), end: open.end_time.slice(0, 5) } : { start: '09:00', end: '18:00' };
  }, [weeklyHours]);

  const openDayView = async (d) => {
    const key = dateKey(d);
    const wOpen = weeklyOpenOn(d.getDay());
    const ex = exceptions[key];
    const isBlocked = ex && ex._dirty !== 'delete' && ex.is_closed;
    if (isBlocked) return;

    let hStart, hEnd;
    if (ex && !ex.is_closed && ex._dirty !== 'delete') {
      hStart = ex.start_time.slice(0, 5); hEnd = ex.end_time.slice(0, 5);
    } else if (wOpen) {
      const row = weeklyHours.find((r) => r.weekday === d.getDay());
      hStart = row.start_time.slice(0, 5); hEnd = row.end_time.slice(0, 5);
    } else {
      setOpenEditor({ date: key, start: usualHours.start, end: usualHours.end });
      return;
    }

    const dayStart = new Date(d); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(d); dayEnd.setHours(23, 59, 59, 999);
    const [brRes, bkRes] = await Promise.all([
      supabase.from('barber_breaks')
        .select('id,start_time,end_time,label')
        .eq('barber_id', barber.id).eq('date', key).order('start_time'),
      supabase.from('bookings')
        .select('id,starts_at,ends_at,status')
        .eq('barber_id', barber.id)
        .gte('starts_at', dayStart.toISOString())
        .lte('starts_at', dayEnd.toISOString())
        .in('status', ['pending', 'confirmed', 'completed']),
    ]);

    setDayView({
      date: key, hStart, hEnd,
      breaks: brRes.data || [],
      bookings: bkRes.data || [],
      selected: new Set(),
    });
  };

  const blockDayFromView = async () => {
    if (!dayView) return;
    const wOpen = weeklyOpenOn(new Date(dayView.date + 'T00:00:00').getDay());
    if (wOpen) {
      // Batch 3.3 — Bookings always win. Refuse to close a day that
      // has any existing non-cancelled booking on it; the barber must
      // cancel or reschedule those bookings first.
      const dayStart = new Date(`${dayView.date}T00:00:00`).toISOString();
      const dayEnd = new Date(`${dayView.date}T23:59:59`).toISOString();
      const { data: existing } = await supabase
        .from('bookings')
        .select('id,starts_at,customer:customers(name)')
        .eq('barber_id', barber.id)
        .in('status', ['pending', 'confirmed', 'completed'])
        .gte('starts_at', dayStart).lte('starts_at', dayEnd);
      if ((existing || []).length) {
        const list = existing.slice(0, 3).map((b) => {
          const t = new Date(b.starts_at);
          return `${t.getHours().toString().padStart(2, '0')}:${t.getMinutes().toString().padStart(2, '0')} · ${b.customer?.name || 'Customer'}`;
        }).join(', ');
        setError(
          `Can\u2019t close this day — you have ${existing.length} booking${existing.length === 1 ? '' : 's'} on it: ` +
          list + (existing.length > 3 ? ` (+${existing.length - 3} more)` : '') +
          '. Reschedule or cancel those bookings first.'
        );
        return;
      }
      setExceptions({
        ...exceptions,
        [dayView.date]: { date: dayView.date, is_closed: true, start_time: null, end_time: null, _dirty: 'insert' },
      });
    } else {
      const ex = exceptions[dayView.date];
      if (ex) setExceptions({ ...exceptions, [dayView.date]: { ...ex, _dirty: 'delete' } });
    }
    setDayView(null);
  };

  const unblockDay = (d) => {
    const key = dateKey(d);
    const ex = exceptions[key];
    if (!ex) return;
    setExceptions({ ...exceptions, [key]: { ...ex, _dirty: 'delete' } });
  };

  const toggleSlotInDayView = (slotMin) => {
    if (!dayView) return;
    const newSel = new Set(dayView.selected);
    if (newSel.has(slotMin)) newSel.delete(slotMin); else newSel.add(slotMin);
    setDayView({ ...dayView, selected: newSel });
  };

  const dayViewStep = slotInterval || 30;
  const dayViewSlots = useMemo(() => {
    if (!dayView) return [];
    const s = timeToMin(dayView.hStart);
    const e = timeToMin(dayView.hEnd);
    const out = [];
    for (let t = s; t + dayViewStep <= e; t += dayViewStep) out.push(t);
    return out;
  }, [dayView, dayViewStep]);

  const slotState = (t) => {
    if (!dayView) return 'free';
    const end = t + dayViewStep;
    for (const b of dayView.bookings) {
      const bs = new Date(b.starts_at); const be = new Date(b.ends_at);
      const bm = bs.getHours() * 60 + bs.getMinutes();
      const em = be.getHours() * 60 + be.getMinutes();
      if (bm < end && em > t) return 'booked';
    }
    for (const br of dayView.breaks) {
      const bm = timeToMin(br.start_time);
      const em = timeToMin(br.end_time);
      if (bm < end && em > t) return 'break';
    }
    return 'free';
  };

  const saveBreaksFromDayView = async () => {
    if (!dayView) return;
    const slots = [...dayView.selected].sort((a, b) => a - b);
    if (slots.length === 0) { setDayView(null); return; }
    const rows = [];
    let s = slots[0], e = slots[0] + dayViewStep;
    for (let i = 1; i < slots.length; i++) {
      if (slots[i] === e) e += dayViewStep;
      else {
        rows.push({ barber_id: barber.id, date: dayView.date, start_time: `${minToTime(s)}:00`, end_time: `${minToTime(e)}:00` });
        s = slots[i]; e = slots[i] + dayViewStep;
      }
    }
    rows.push({ barber_id: barber.id, date: dayView.date, start_time: `${minToTime(s)}:00`, end_time: `${minToTime(e)}:00` });

    // Batch 3.3 — Bookings always win. Refuse the save if any proposed
    // break range collides with an existing non-cancelled booking on
    // this barber. Listing the customer(s) makes it clear WHY the save
    // was refused so the barber can cancel/reschedule first.
    const dayStart = new Date(`${dayView.date}T00:00:00`).toISOString();
    const dayEnd = new Date(`${dayView.date}T23:59:59`).toISOString();
    const { data: existing } = await supabase
      .from('bookings')
      .select('id,starts_at,ends_at,customer:customers(name)')
      .eq('barber_id', barber.id)
      .in('status', ['pending', 'confirmed', 'completed'])
      .gte('starts_at', dayStart).lte('starts_at', dayEnd);
    const collisions = [];
    for (const b of (existing || [])) {
      const bs = new Date(b.starts_at); const be = new Date(b.ends_at);
      const bm = bs.getHours() * 60 + bs.getMinutes();
      const em = be.getHours() * 60 + be.getMinutes();
      for (const r of rows) {
        const rs = timeToMin(r.start_time.slice(0, 5));
        const re = timeToMin(r.end_time.slice(0, 5));
        if (bm < re && em > rs) {
          collisions.push(`${bs.getHours().toString().padStart(2, '0')}:${bs.getMinutes().toString().padStart(2, '0')} · ${b.customer?.name || 'Customer'}`);
          break;
        }
      }
    }
    if (collisions.length) {
      setError(
        `Can\u2019t add a break here — you have ${collisions.length} booking${collisions.length === 1 ? '' : 's'} that would clash: ` +
        collisions.slice(0, 3).join(', ') +
        (collisions.length > 3 ? ` (+${collisions.length - 3} more)` : '') +
        '. Reschedule or cancel those bookings first.'
      );
      return;
    }

    const { error: brErr } = await supabase.from('barber_breaks').insert(rows);
    if (brErr) { setError(brErr.message); return; }
    await openDayView(new Date(dayView.date + 'T00:00:00'));
  };

  const removeBreak = async (id) => {
    const { error: dErr } = await supabase.from('barber_breaks').delete().eq('id', id);
    if (dErr) { setError(dErr.message); return; }
    await openDayView(new Date(dayView.date + 'T00:00:00'));
  };

  const cycleDay = (d) => {
    const key = dateKey(d);
    const wOpen = weeklyOpenOn(d.getDay());
    const ex = exceptions[key];
    const isBlocked = ex && ex._dirty !== 'delete' && ex.is_closed;
    if (isBlocked) { unblockDay(d); return; }
    if (wOpen || (ex && !ex.is_closed && ex._dirty !== 'delete')) { openDayView(d); return; }
    setOpenEditor({ date: key, start: usualHours.start, end: usualHours.end });
  };

  const saveForceOpen = () => {
    if (!openEditor) return;
    const { date, start, end } = openEditor;
    if (!start || !end || start >= end) { setError('End time must be after start time.'); return; }
    setExceptions({
      ...exceptions,
      [date]: { date, is_closed: false, start_time: `${start}:00`, end_time: `${end}:00`, _dirty: 'insert' },
    });
    setOpenEditor(null);
    setError('');
  };

  // View navigation bounds
  const weekStart = useMemo(() => startOfWeek(viewAnchor), [viewAnchor]);
  const monthStart = useMemo(() => new Date(viewAnchor.getFullYear(), viewAnchor.getMonth(), 1), [viewAnchor]);
  const todayWeekStart = useMemo(() => startOfWeek(todayMidnight()), []);
  const todayMonthStart = useMemo(() => { const t = todayMidnight(); return new Date(t.getFullYear(), t.getMonth(), 1); }, []);
  const canPrevWeek = weekStart > todayWeekStart;
  const canNextWeek = (() => { const n = new Date(weekStart); n.setDate(n.getDate() + 7); return !isBeyondHorizon(n); })();
  const canPrevMonth = monthStart > todayMonthStart;
  const canNextMonth = (() => { const n = new Date(monthStart); n.setMonth(n.getMonth() + 1); return !isBeyondHorizon(n); })();
  const canPrevDay = viewAnchor > todayMidnight();
  const canNextDay = !isBeyondHorizon(new Date(viewAnchor.getTime() + 86400000));
  const canGoToday = viewAnchor.toDateString() !== new Date().toDateString();

  const shiftAnchor = (deltaDays) => { const n = new Date(viewAnchor); n.setDate(n.getDate() + deltaDays); setViewAnchor(n); };
  const shiftMonth = (deltaMonths) => { const n = new Date(viewAnchor); n.setMonth(n.getMonth() + deltaMonths); setViewAnchor(n); };

  const rangeLabel = useMemo(() => {
    if (viewMode === 'day') return fmtFullDate(viewAnchor);
    if (viewMode === 'month') return new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(viewAnchor);
    const days = weekDays(viewAnchor);
    const [a, b] = [days[0], days[6]];
    const sameMonth = a.getMonth() === b.getMonth();
    const sameYear = a.getFullYear() === b.getFullYear();
    if (sameMonth) return `${a.getDate()}–${b.getDate()} ${MONTH_SHORT[a.getMonth()]} ${a.getFullYear()}`;
    if (sameYear) return `${a.getDate()} ${MONTH_SHORT[a.getMonth()]} – ${b.getDate()} ${MONTH_SHORT[b.getMonth()]} ${a.getFullYear()}`;
    return `${a.getDate()} ${MONTH_SHORT[a.getMonth()]} ${a.getFullYear()} – ${b.getDate()} ${MONTH_SHORT[b.getMonth()]} ${b.getFullYear()}`;
  }, [viewMode, viewAnchor]);

  const setView = (next) => {
    if (next === viewMode) return;
    if (next === 'day') setViewAnchor(todayMidnight());
    setViewMode(next);
  };

  useEffect(() => {
    if (viewMode !== 'day' || !barber) return;
    const key = dateKey(viewAnchor);
    const wOpen = weeklyOpenOn(viewAnchor.getDay());
    const ex = exceptions[key];
    const isBlocked = ex && ex._dirty !== 'delete' && ex.is_closed;
    const isForceOpen = ex && !ex.is_closed && ex._dirty !== 'delete';
    setDayView(null);
    if (isBlocked) return;
    if (!wOpen && !isForceOpen) return;
    openDayView(viewAnchor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, viewAnchor, barber?.id, exceptions[dateKey(viewAnchor)]]);

  const renderDayTile = (d) => {
    const key = dateKey(d);
    const wOpen = weeklyOpenOn(d.getDay());
    const ex = exceptions[key];
    let state;
    if (ex && ex._dirty === 'delete') state = wOpen ? 'open' : 'closed';
    else if (ex && !ex.is_closed) state = 'force';
    else if (ex && ex.is_closed) state = 'block';
    else state = wOpen ? 'open' : 'closed';
    const isToday = d.toDateString() === new Date().toDateString();
    const past = isPastDay(d);
    return (
      <button
        key={key}
        type="button"
        className={`day ${state} ${isToday ? 'today' : ''} ${past ? 'past' : ''}`}
        onClick={() => !past && cycleDay(d)}
        disabled={past}
        data-testid={`sched-day-${key}`}
      >
        <div className="dow">{WEEKDAY_SHORT[d.getDay()]}</div>
        <div className="dn">{d.getDate()}</div>
        <div className="mo">{MONTH_SHORT[d.getMonth()]}</div>
        {state === 'force' && ex && (
          <div className="tm">{ex.start_time?.slice(0, 5)}-{ex.end_time?.slice(0, 5)}</div>
        )}
      </button>
    );
  };

  const save = async () => {
    if (!barber) return;
    setSaving(true); setError(''); setFlash('');
    try {
      const { error: bErr } = await supabase
        .from('barbers')
        .update({
          min_notice_hours: minNotice,
          booking_window_days: bookingWindow,
          slot_interval_minutes: slotInterval || null,
          booking_buffer_minutes: bookingBuffer,
        })
        .eq('id', barber.id);
      if (bErr) throw bErr;

      const inserts = [];
      const deletes = [];
      Object.values(exceptions).forEach((ex) => {
        if (ex._dirty === 'insert') {
          inserts.push({
            barber_id: barber.id, date: ex.date, is_closed: ex.is_closed,
            start_time: ex.start_time, end_time: ex.end_time,
          });
        } else if (ex._dirty === 'delete' && ex.id) {
          deletes.push(ex.id);
        }
      });

      if (deletes.length) {
        const { error: dErr } = await supabase
          .from('barber_date_exceptions').delete().in('id', deletes);
        if (dErr) throw dErr;
      }
      if (inserts.length) {
        // upsert on (barber_id, date) so re-toggling a day just updates it
        const { error: iErr } = await supabase
          .from('barber_date_exceptions')
          .upsert(inserts, { onConflict: 'barber_id,date' });
        if (iErr) throw iErr;
      }

      setFlash('Schedule saved.');
      await load();
      setTimeout(() => setFlash(''), 2500);
    } catch (e) {
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const onNavPrev = () => (viewMode === 'month' ? shiftMonth(-1) : shiftAnchor(viewMode === 'week' ? -7 : -1));
  const onNavNext = () => (viewMode === 'month' ? shiftMonth(1) : shiftAnchor(viewMode === 'week' ? 7 : 1));
  const canPrev = viewMode === 'week' ? canPrevWeek : viewMode === 'month' ? canPrevMonth : canPrevDay;
  const canNext = viewMode === 'week' ? canNextWeek : viewMode === 'month' ? canNextMonth : canNextDay;

  return (
    <AppShell>
      <main className="main">
      <div className="topbar">
        <div>
          <h2>{targetBarberId ? (barber ? `${barber.name}'s schedule` : 'Schedule') : 'My schedule'}</h2>
          <div className="sub">
            {targetBarberId && (callerRole === 'shop_admin' || callerRole === 'master_admin')
              ? 'Editing another barber. They can also change this themselves from My schedule.'
              : 'Control when customers can book you, and mark holidays or one-off open days.'}
          </div>
        </div>
        <button
          className="btn p"
          style={{ marginLeft: 'auto' }}
          onClick={save}
          disabled={saving || !barber || !dirty}
          data-testid="sched-save"
        >
          <Save size={14} /> {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
        </button>
      </div>

      {dirty && barber && (
        <DirtyBar
          barberName={barber.name}
          saving={saving}
          onDiscard={() => { load(); }}
          onSave={save}
        />
      )}

      <div className="body">
        {loading && <div className="mute">Loading...</div>}

        {!loading && !barber && (
          <div className="empty" data-testid="sched-no-barber">
            {targetBarberId ? (
              <>Barber not found or you don&rsquo;t have permission to edit them.</>
            ) : (callerRole === 'shop_admin' || callerRole === 'master_admin') ? (
              <>
                You don&rsquo;t have a barber profile yet. If you also cut hair,
                add yourself as a barber from the{' '}
                <a href="/barbers" data-testid="sched-add-self-link">Barbers page</a>.
              </>
            ) : (
              <>You don&rsquo;t have a barber profile. Ask your shop admin to add you.</>
            )}
          </div>
        )}

        {!loading && barber && (
          <>
            {error && <div className="banner err" data-testid="sched-err">{error}</div>}
            {flash && <div className="banner ok" data-testid="sched-ok">{flash}</div>}

            <PolicyPanel
              minNotice={minNotice} setMinNotice={setMinNotice}
              bookingWindow={bookingWindow} setBookingWindow={setBookingWindow}
              slotInterval={slotInterval} setSlotInterval={setSlotInterval}
              bookingBuffer={bookingBuffer} setBookingBuffer={setBookingBuffer}
            />

            <section className="panel" style={{ marginTop: 14 }} data-testid="sched-dates-panel">
              <div className="ph">
                <h3>Dates</h3>
                <div className="legend">
                  <span className="lg open">Open</span>
                  <span className="lg closed">Off day</span>
                  <span className="lg block">Blocked (holiday)</span>
                  <span className="lg force">Force open</span>
                </div>
                <ViewNav
                  viewMode={viewMode} setView={setView}
                  rangeLabel={rangeLabel}
                  onPrev={onNavPrev} onNext={onNavNext}
                  onToday={() => setViewAnchor(todayMidnight())}
                  canPrev={canPrev} canNext={canNext} canGoToday={canGoToday}
                />
              </div>
              <div className="panel-body">
                <p className="mute" style={{ margin: '0 0 12px', fontSize: 12.5 }}>
                  {viewMode === 'day'
                    ? 'Tap slots to select, then Mark as break. Use the arrows to move between days.'
                    : 'Click a day to open availability. Normally-open days become blocked; normally-off days open an editor so you can force them open with custom hours.'}
                </p>

                {viewMode === 'week' && (
                  <div className="date-grid wk" data-testid="sched-cal-week">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                      <div key={d} className="dow-h">{d}</div>
                    ))}
                    {weekDays(viewAnchor).map(renderDayTile)}
                  </div>
                )}

                {viewMode === 'month' && (
                  <div className="date-grid mo7" data-testid="sched-cal-month">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                      <div key={d} className="dow-h">{d}</div>
                    ))}
                    {monthGridDays(viewAnchor).map((d) => {
                      const inMonth = d.getMonth() === viewAnchor.getMonth();
                      if (!inMonth) return <div key={`b-${d.toISOString()}`} className="day blank" aria-hidden="true" />;
                      return renderDayTile(d);
                    })}
                  </div>
                )}

                {viewMode === 'day' && (
                  <div className="sched-day-inline" data-testid="sched-day-inline">
                    {dayView ? (
                      <>
                        <p className="mute" style={{ marginBottom: 10, fontSize: 'var(--fs-sm)' }}>
                          {dayView.hStart}–{dayView.hEnd} · {dayViewStep}-minute grid
                        </p>
                        <AvailabilityGrid
                          dayView={dayView}
                          dayViewSlots={dayViewSlots}
                          slotStateFor={slotState}
                          toggleSlot={toggleSlotInDayView}
                          onRemoveBreak={removeBreak}
                        />
                        <AvailabilityActions
                          context="inline"
                          dayView={dayView}
                          weeklyOpenOnDay={weeklyOpenOn(new Date(dayView.date + 'T00:00:00').getDay())}
                          onBlockDay={blockDayFromView}
                          onSaveBreaks={saveBreaksFromDayView}
                        />
                      </>
                    ) : (
                      <div className="empty" data-testid="sched-day-inline-empty" style={{ padding: '32px 20px' }}>
                        {(() => {
                          const key = dateKey(viewAnchor);
                          const wOpen = weeklyOpenOn(viewAnchor.getDay());
                          const ex = exceptions[key];
                          const isBlocked = ex && ex._dirty !== 'delete' && ex.is_closed;
                          if (isBlocked) {
                            return (
                              <>
                                <div style={{ marginBottom: 12 }}>This day is blocked.</div>
                                <button className="btn s" onClick={() => unblockDay(viewAnchor)} data-testid="sched-inline-unblock">Unblock</button>
                              </>
                            );
                          }
                          if (!wOpen && !(ex && !ex.is_closed && ex._dirty !== 'delete')) {
                            return (
                              <>
                                <div style={{ marginBottom: 12 }}>This is normally your day off.</div>
                                <button
                                  className="btn s"
                                  onClick={() => setOpenEditor({ date: key, start: usualHours.start, end: usualHours.end })}
                                  data-testid="sched-inline-force-open"
                                >Force open with custom hours</button>
                              </>
                            );
                          }
                          return <div className="mute">Loading availability…</div>;
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        {openEditor && (
          <ForceOpenModal
            openEditor={openEditor}
            setOpenEditor={setOpenEditor}
            onSave={saveForceOpen}
          />
        )}

        {dayView && viewMode !== 'day' && (
          <div className="sched-modal-back" onClick={() => setDayView(null)}>
            <div className="sched-modal dv-modal" onClick={(e) => e.stopPropagation()} data-testid="sched-day-view">
              <h3 style={{ marginBottom: 4 }}>My availability</h3>
              <p className="mute" style={{ marginBottom: 12 }}>
                {fmtFullDate(new Date(dayView.date + 'T00:00:00'))}
              </p>
              <p className="mute" style={{ marginBottom: 12 }}>
                {dayView.hStart}–{dayView.hEnd} · {dayViewStep}-minute grid · Tap slots to select, then Mark as break.
              </p>
              <AvailabilityGrid
                dayView={dayView}
                dayViewSlots={dayViewSlots}
                slotStateFor={slotState}
                toggleSlot={toggleSlotInDayView}
                onRemoveBreak={removeBreak}
              />
              <AvailabilityActions
                context="modal"
                dayView={dayView}
                weeklyOpenOnDay={weeklyOpenOn(new Date(dayView.date + 'T00:00:00').getDay())}
                onBlockDay={blockDayFromView}
                onSaveBreaks={saveBreaksFromDayView}
                onClose={() => setDayView(null)}
              />
            </div>
          </div>
        )}
      </div>
      </main>
    </AppShell>
  );
};

export default BarberSchedule;
